import type { EngineResult, EstimateItem } from './estimate.types';
import { createEstimateItem } from './estimateItemFactory';
import type { MarginShieldAuditResult } from './marginShieldAudit';
import {
  calculateApprovedOfferPricing,
  type ApprovedOfferPricing,
  type ManualEstimateItem,
} from './manualEstimateItems';
import {
  normalizeMaterialMode,
  type CanonicalMaterialMode,
  type MaterialClientLine,
  type MaterialMode,
} from './materialMode';
import {
  buildClientMaterialLines,
  calculateClientPricingSummary,
  type SettlementMode,
} from './simpleEstimateEngine';

export type MarginShieldDecisionStatus = 'suggested' | 'added' | 'skipped' | 'checked';
export type MarginShieldDecisions = Record<string, MarginShieldDecisionStatus>;

const CLIENT_SUPPLIED_MATERIAL_ONLY_ALERT_IDS = new Set([
  'missing_cutting_waste',
]);

export interface ApprovedOfferViewModel {
  items: EstimateItem[];
  marginShieldAddedItems: EstimateItem[];
  pricing: ApprovedOfferPricing;
  materialMode: CanonicalMaterialMode;
  materialLines: MaterialClientLine[];
  /**
   * Prace, ktore wykonawca oznaczyl jako "Mam w cenie" (decyzja `checked`).
   * Sa czescia zakresu, ale NIE sa osobno platne — nie wchodza do `items`
   * ani do `pricing`. Sluza wylacznie do pokazania klientowi, ze praca jest
   * objeta oferta, zeby dokument nie sugerowal, ze jej nie obejmuje.
   */
  includedScopeLabels: string[];
  excludedScopeLabels: string[];
  unresolvedScopeLabels: string[];
}

export function setMarginShieldDecision(
  current: MarginShieldDecisions,
  alertId: string,
  status: MarginShieldDecisionStatus
): MarginShieldDecisions {
  const stableAlertId = alertId.trim();
  if (!stableAlertId || current[stableAlertId] === status) return current;

  return {
    ...current,
    [stableAlertId]: status,
  };
}

/** Display-only status; persisted decisions and approved pricing stay unchanged. */
export function resolveMarginShieldDisplayStatus(
  alertId: string,
  decision: MarginShieldDecisionStatus | undefined,
  materialMode: MaterialMode
): MarginShieldDecisionStatus | 'informational' {
  if (isClientSuppliedMaterialOnlyAlert(alertId, normalizeMaterialMode(materialMode))) {
    return 'informational';
  }
  return decision ?? 'suggested';
}

export function getMarginShieldItemId(alertId: string): string {
  return `margin_shield_${alertId.trim()}`;
}

export function buildApprovedOfferViewModel(input: {
  estimate: EngineResult;
  marginShieldAudit: MarginShieldAuditResult;
  workingAreaM2: number;
  marginShieldDecisions: Record<string, MarginShieldDecisionStatus>;
  manualItems?: ManualEstimateItem[];
  materialMode: MaterialMode;
  materialClientPrice?: number;
  settlementMode: SettlementMode;
}): ApprovedOfferViewModel {
  const materialMode = normalizeMaterialMode(input.materialMode);
  const marginShieldAddedItems = buildMarginShieldAddedItems({
    audit: input.marginShieldAudit,
    areaM2: input.workingAreaM2,
    decisions: input.marginShieldDecisions,
    materialMode,
  });
  const includedScopeLabels = buildIncludedScopeLabels({
    audit: input.marginShieldAudit,
    decisions: input.marginShieldDecisions,
    materialMode,
  });
  const items = [
    ...input.estimate.items.filter((item) =>
      isApprovedExistingItem(item, input.marginShieldDecisions, materialMode)
    ),
    ...marginShieldAddedItems,
    ...(input.manualItems ?? []),
  ];
  const workItems = items.filter(
    (item) => !(item.source === 'manual' && item.itemType === 'material')
  );
  const basePricing = calculateClientPricingSummary({
    items: workItems,
    materialSuggestions: input.estimate.meta.materialSuggestions,
    materialMode,
    materialMarkupPercent: 0,
    settlementMode: input.settlementMode,
  });
  const hasManualClientMaterialPrice =
    materialMode === 'CONTRACTOR_MATERIALS' &&
    typeof input.materialClientPrice === 'number' &&
    Number.isFinite(input.materialClientPrice) &&
    input.materialClientPrice >= 0;
  const baseMaterialNet = hasManualClientMaterialPrice
    ? input.materialClientPrice ?? 0
    : basePricing.clientVisibleMaterialTotal;
  const pricing = calculateApprovedOfferPricing({
    items,
    baseMaterialNet,
    vatRate: basePricing.vatRate,
  });
  const materialLines = hasManualClientMaterialPrice
    ? [{
        label: 'Materiały wg ustaleń',
        quantityLabel: 'zestaw',
        clientPrice: baseMaterialNet,
      }]
    : buildClientMaterialLines({
        materialSuggestions: input.estimate.meta.materialSuggestions,
        materialMode,
        materialMarkupPercent: 0,
      });

  return {
    items,
    marginShieldAddedItems,
    pricing,
    materialMode,
    materialLines,
    includedScopeLabels,
    excludedScopeLabels: buildUnpricedScopeLabels(input.marginShieldAudit, input.marginShieldDecisions, materialMode, 'skipped'),
    unresolvedScopeLabels: buildUnpricedScopeLabels(input.marginShieldAudit, input.marginShieldDecisions, materialMode, 'suggested'),
  };
}

function buildUnpricedScopeLabels(
  audit: MarginShieldAuditResult,
  decisions: MarginShieldDecisions,
  materialMode: CanonicalMaterialMode,
  status: 'skipped' | 'suggested'
): string[] {
  return [...new Set(audit.alerts
    .filter((alert) => (decisions[alert.id] ?? 'suggested') === status)
    .filter((alert) => !isClientSuppliedMaterialOnlyAlert(alert.id, materialMode))
    .map((alert) => alert.missingItem.trim())
    .filter(Boolean))];
}

/**
 * Etykiety pozycji z decyzja `checked` ("Mam w cenie"), w kolejnosci alertow.
 * `added` jest pomijane celowo — to normalna platna pozycja oferty i nie moze
 * pojawic sie drugi raz jako "w cenie". `suggested` i `skipped` nie sa decyzja
 * wykonawcy o zawarciu pracy w cenie, wiec tez sie nie kwalifikuja.
 */
function buildIncludedScopeLabels(input: {
  audit: MarginShieldAuditResult;
  decisions: Record<string, MarginShieldDecisionStatus>;
  materialMode: CanonicalMaterialMode;
}): string[] {
  const labels = input.audit.alerts
    .filter((alert) => input.decisions[alert.id] === 'checked')
    .filter((alert) => !isClientSuppliedMaterialOnlyAlert(alert.id, input.materialMode))
    .map((alert) => alert.missingItem.trim())
    .filter((label) => label.length > 0);

  return [...new Set(labels)];
}

function buildMarginShieldAddedItems(input: {
  audit: MarginShieldAuditResult;
  areaM2: number;
  decisions: Record<string, MarginShieldDecisionStatus>;
  materialMode: CanonicalMaterialMode;
}): EstimateItem[] {
  return input.audit.alerts
    .filter((alert) => input.decisions[alert.id] === 'added')
    .filter((alert) => !isClientSuppliedMaterialOnlyAlert(alert.id, input.materialMode))
    .map((alert, index) => {
      const isAreaBased = alert.estimatedImpact.type === 'perM2' && input.areaM2 > 0;
      const isLinear = alert.estimatedImpact.type === 'perMb' && (alert.quantity ?? 0) > 0;
      const isWasteCleanup = alert.id === 'missing_waste_cleanup';
      const item = createEstimateItem({
        id: getMarginShieldItemId(alert.id),
        label: alert.missingItem,
        category: 'margin_shield',
        quantity: isLinear ? (alert.quantity ?? 0) : isAreaBased ? input.areaM2 : 1,
        unit: isLinear ? 'mb' : isAreaBased ? 'm²' : isWasteCleanup ? 'komplet' : 'szt',
        unitPrice: isLinear || isAreaBased ? alert.estimatedImpact.amountNet : alert.estimatedHiddenLoss,
        pricingMode: isLinear ? 'linear' : isAreaBased ? 'area' : 'flat',
        order: 700 + index,
      });

      return {
        ...item,
        source: 'margin_shield',
      };
    });
}

function isApprovedExistingItem(
  item: EstimateItem,
  decisions: Record<string, MarginShieldDecisionStatus>,
  materialMode: CanonicalMaterialMode
): boolean {
  if (!item.id.startsWith('margin_shield_')) return true;
  const alertId = item.id.replace(/^margin_shield_/, '');
  if (isClientSuppliedMaterialOnlyAlert(alertId, materialMode)) return false;
  return decisions[alertId] === 'added';
}

function isClientSuppliedMaterialOnlyAlert(
  alertId: string,
  materialMode: CanonicalMaterialMode
): boolean {
  const materialsAreNotBillable =
    materialMode === 'CLIENT_MATERIALS' || materialMode === 'INFO_MATERIALS';

  return materialsAreNotBillable && CLIENT_SUPPLIED_MATERIAL_ONLY_ALERT_IDS.has(alertId);
}
